/**
 * SegmentDetailModal.jsx - Market Segment Edit Modal
 * 
 * Standalone modal component for editing market segments.
 * Extracted from ProductDetailPage for use with EntityModalContext.
 * Delete functionality moved to card trash icon with confirmation.
 * 
 * Uses fixed positioning with z-index control for proper modal stacking.
 */
import React, { useState, useEffect } from 'react';
import { Globe, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MarkdownEditor } from '../ui/markdown-editor';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { ModalZIndexProvider } from '../../contexts/ModalZIndexContext';
import { toast } from 'sonner';

const SegmentDetailModal = ({
  isOpen,
  segment,
  productId,
  onSave,
  onClose,
  canEdit = true,
  zIndex,
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [formData, setFormData] = useState({
    segment_id: '',
    title: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);
  
  // Sync form data when segment changes
  useEffect(() => {
    if (segment) {
      setFormData({
        segment_id: segment.segment_id || '',
        title: segment.title || '',
        description: segment.description || '',
      });
    }
  }, [segment]);
  
  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Segment title is required');
      return;
    }
    
    setSaving(true);
    try {
      await onSave?.({
        segment_id: formData.segment_id,
        title: formData.title.trim(),
        description: formData.description.trim(),
      });
      onClose?.();
    } catch (err) {
      console.error('Failed to save segment:', err);
      // Error toast handled by parent
    } finally {
      setSaving(false);
    }
  };
  
  const isNewSegment = !segment?.title;
  
  if (!isOpen) return null;
  
  return (
    <ModalZIndexProvider zIndex={zIndex}>
      <div className="fixed inset-0" style={{ zIndex }}>
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/60" 
          onClick={onClose}
        />
        
        {/* Modal - centered, smaller than full-page modals */}
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div 
            className={cn(
              "relative w-full max-w-3xl rounded-xl shadow-2xl flex flex-col overflow-hidden",
              colors.bgCard
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={cn("flex items-center justify-between px-6 py-4 border-b", colors.borderPrimary)}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                </div>
                <div>
                  <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>
                    {isNewSegment ? 'Add Market Segment' : 'Edit Market Segment'}
                  </h2>
                  {!isNewSegment && (
                    <p className={cn("text-xs font-mono", colors.textMuted)}>
                      {formData.segment_id}
                    </p>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={onClose}
                className="h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
              {/* Title */}
              <div>
                <label className={cn("text-sm font-medium", colors.textSecondary)}>
                  Segment Title <span className="text-red-500">*</span>
                </label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Enterprise SaaS, SMB Retail"
                  className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                  disabled={!canEdit}
                  data-testid="segment-title-input"
                />
              </div>
              
              {/* Description - WYSIWYG Markdown Editor */}
              <div>
                <label className={cn("text-sm font-medium", colors.textSecondary)}>
                  Description
                </label>
                <div className="mt-1">
                  <MarkdownEditor
                    content={formData.description}
                    onChange={(md) => setFormData(prev => ({ ...prev, description: md }))}
                    placeholder="Describe this market segment, its characteristics, and target customers..."
                    minHeight={200}
                    disabled={!canEdit}
                  />
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className={cn("flex items-center justify-end px-6 py-4 border-t", colors.borderPrimary)}>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={onClose}
                  data-testid="segment-cancel-btn"
                >
                  Cancel
                </Button>
                {canEdit && (
                  <Button 
                    onClick={handleSave}
                    disabled={saving || !formData.title.trim()}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                    data-testid="segment-save-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      'Save Segment'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalZIndexProvider>
  );
};

export default SegmentDetailModal;
